// ClusterParticipationChart.jsx

import { useRef } from "react";
import React from "react";
import { ResponsiveBar } from "@nivo/bar";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import ExportPopover from "./ExportPopover";
import { useChartExport } from "../hooks/useChartExport";

// A dictionary to translate negative sentinel -> user-friendly reason
const negativeReasons = {
    "-1": "Top-Bottom",
    "-2": "Within-Group\nDAT (binary)",
    "-3": "N<10",
    "-4": "Within-Group\nDAT (non-binary)",
    "-5": "Cross Student Group"
  };
  const ALL_CLUSTERS = [
    "Arts, A/V Technology and Communications",
    "Health Science",
    "Business Management and Administration",
    "Architecture and Construction",
    "Finance",
    "Human Services",
    "Transportation, Distribution and Logistics",
    "Agriculture, Food and Natural Resources",
    "Marketing",
    "Law, Public Safety, Corrections and Security",
    "Manufacturing",
    "Science, Technology, Engineering and Mathematics",
    "Education and Training",
    "Government and Public Administration",
    "Hospitality and Tourism",
    "Information Technology",
  ];


  // Abbreviated Cluster Names
const ABBREVIATED_CLUSTERS = {
  "Arts, A/V Technology and Communications": "Arts & A/V Tech",
  "Health Science": "Health Science",
  "Business Management and Administration": "Business Admin",
  "Architecture and Construction": "Architecture & Construction",
  "Finance": "Finance",
  "Human Services": "Human Services",
  "Transportation, Distribution and Logistics": "Transport & Logistics",
  "Agriculture, Food and Natural Resources": "Agriculture & Natural Resources",
  "Marketing": "Marketing",
  "Law, Public Safety, Corrections and Security": "Law & Public Safety",
  "Manufacturing": "Manufacturing",
  "Science, Technology, Engineering and Mathematics": "STEM",
  "Education and Training": "Education & Training",
  "Government and Public Administration": "Government & Public Admin",
  "Hospitality and Tourism": "Hospitality & Tourism",
  "Information Technology": "Info Technology",
};

// Function to get cluster name based on width
const getClusterName = (clusterName, chartWidth, realCount) => {
  if (chartWidth < 800 || realCount > 13) {
    return ABBREVIATED_CLUSTERS[clusterName] || clusterName;
  }
  return clusterName;
};

// Helper function to format student group phrasing
const getStudentGroupDisplay = (group) => {
  // Define renaming rules
  const renameMap = {
    "All Students": "students across all groups", // Custom label for "All Students"
    "Student with Disabilities": "Students with Disabilities",
    "Student without Disabilities": "Students without Disabilities",
    "ELL": "English Language Learners",
    "Non-ELL": "Non-English Language Learners",
    "FRL": "Free and Reduced Lunch",
    "Non-FRL": "Non-Free and Reduced Lunch",
    "Section504": "Section 504 Students",
    "Non-Section504": "Non-Section 504 Students",
    "Military Parent": "Students with Military Parents",
    "Non Military Parent": "Students without Military Parents",
    "Hispanic/Latino of any race(s)": "Hispanic or Latino Students",
  };

  // Check if group exists in the rename map
  if (renameMap[group]) {
    return renameMap[group];
  }

  // Groups that don't need "students" appended
  const exceptions = ["All Students"];

  if (exceptions.includes(group)) {
    return group; // Use as is
  }

  // Default case: Append "students"
  return `${group} students`;
};
    

const formatPercent = (value) => {
    if (value === null || value === undefined) {
      return "No data";
    }
    if (typeof value === "number" && value >= 0) {
      return `${value.toFixed(0)}%`;
    }
    // fallback
    return "Masked";
  };


  
  const splitTextOnWords = (text, maxLength) => {
    if (!text) return [];
    const words = text.split(" ");
    const lines = [];
    let currentLine = "";
  
    words.forEach((word) => {
      if ((currentLine + (currentLine ? " " : "") + word).length > maxLength) {
        if (currentLine) {  // Only push if currentLine is not empty
          lines.push(currentLine);
        }
        currentLine = word; // Start a new line with the current word
      } else {
        currentLine += (currentLine ? " " : "") + word;
      }
    });
  
    if (currentLine) { // Push the last line if not empty
      lines.push(currentLine);
    }
  
    return lines;
  };


  // Suppose you have something like this in your chart’s code:
function customExcelTable({
  workbook,
  worksheet,
  chartData,
  headers,
  // Optionally pass the same string from prepareDataForClipboard if you want
  tsvString,
}) {
  // Start after row 25
  const startRow = 25;

  // 1) If you prefer the textual approach (like “Title: xyz\nYear: 2020\n...”), 
  //    you can split on newlines and place each line in a single cell across 
  //    multiple rows.

  // For demonstration, let's put the first line in A25, the second in A26, etc.
  if (tsvString) {
    const lines = tsvString.split("\n");
    lines.forEach((line, i) => {
      // Put each line in column A
      worksheet.getCell(`A${startRow + i}`).value = line;
    });
    // The result is a textual block in column A.
    // If you want to parse your tab-delimited content into multiple columns, 
    // just split each line by "\t" and place them across columns.
  }

  // 2) If you prefer a structured approach (like your old CIP logic),
  //    do something like:
  //    Headers in row 25, then data in rows below:

  // Example:
  const secondTableStart = startRow + (tsvString?.split("\n").length ?? 5) + 2;
  // row we begin the “structured table”

  // Let's define some columns
  const customHeaders = ["Cluster", "Year", "Primary Numerator", "Primary Denominator", "Primary %", "Comparison Numerator", "Comparison Denominator", "Comparison %"];
  // Write them in row secondTableStart
  customHeaders.forEach((header, colIdx) => {
    worksheet.getCell(`${String.fromCharCode(65 + colIdx)}${secondTableStart}`).value = header;
    worksheet.getCell(`${String.fromCharCode(65 + colIdx)}${secondTableStart}`).font = { bold: true };
  });

  // Then iterate over chartData
  chartData.forEach((row, i) => {
    const rowNumber = secondTableStart + 1 + i;
    const cluster = row.clusterName;
    const year = row.year;
    const primaryNum = row.numerator ?? "";
    const primaryDen = row.denominator ?? "";
    const primaryPct = row["Cluster Participation %"] != null ? row["Cluster Participation %"] / 100 : null;
    const compNum = row.comparisonNumerator ?? "";
    const compDen = row.comparisonDenominator ?? "";
    const compPct = row.comparisonValue != null ? row.comparisonValue / 100 : null;

    worksheet.getCell(`A${rowNumber}`).value = cluster;
    worksheet.getCell(`B${rowNumber}`).value = year;
    worksheet.getCell(`C${rowNumber}`).value = primaryNum;
    worksheet.getCell(`D${rowNumber}`).value = primaryDen;
    worksheet.getCell(`E${rowNumber}`).value = primaryPct; // stored as fraction
    worksheet.getCell(`F${rowNumber}`).value = compNum;
    worksheet.getCell(`G${rowNumber}`).value = compDen;
    worksheet.getCell(`H${rowNumber}`).value = compPct; // stored as fraction
  });

  // 3) Format as percentages for columns E/H
  worksheet.getColumn("E").numFmt = "0.0%";
  worksheet.getColumn("H").numFmt = "0.0%";

  // column widths
  worksheet.getColumn("A").width = 28;
  worksheet.getColumn("B").width = 12;
  ["C","D","F","G"].forEach((col) => {
    worksheet.getColumn(col).width = 12;
  });
  worksheet.getColumn("E").width = 10;
  worksheet.getColumn("H").width = 10;
}

const ClusterParticipationChart = ({
    clusterData,         // e.g. [{ clusterName, "Cluster Participation %": 23.4 }, ...]
    keys,
    titleText,
    color,   
    comparisonData,
    comparisonColor,
    theme,
    primaryGroup,
    comparisonGroup
  }) => {

     // 1) We'll define a function to produce a string for copying
  const prepareDataForClipboard = (data) => {
    // data is the same array passed to chartData in the hook
    // We can include any fields we want: clusterName, numerator, denominator, etc.

    const lines = [];
    // First line: Title
    lines.push(`Title: ${titleText}\nPrimary Group: ${primaryGroup}\nComparison Group: ${comparisonGroup}\n`);

    // Header row
    lines.push("Cluster\tYear\tPrimary Numerator\tPrimary Denominator\tPrimary %\tComparison Numerator\tComparison Denominator\tComparison %");

    data.forEach((row) => {
      const cluster = row.clusterName || "";
      const year = row.year || "";
      const primaryNum = row.numerator ?? "";
      const primaryDen = row.denominator ?? "";
      const primaryPct = row["Cluster Participation %"] != null ? row["Cluster Participation %"].toFixed(1) + "%" : "";
      const compNum = row.comparisonNumerator ?? "";
      const compDen = row.comparisonDenominator ?? "";
      const compPct = row.comparisonValue != null ? row.comparisonValue.toFixed(1) + "%" : "";

      // e.g.: "Finance\t2023\t12\t34\t35.3%\t10\t30\t33.3%"
      lines.push(`${cluster}\t${year}\t${primaryNum}\t${primaryDen}\t${primaryPct}\t${compNum}\t${compDen}\t${compPct}`);
    });

    return lines.join("\n");
  };


    // 1) Use the hook
  const {
    chartRef,
    exportChartAsPNG,
    exportChartToExcel,
    copyImageToClipboard,
    copyDataToClipboard,
  } = useChartExport({
    defaultFileName: "cluster_participation",
    onAlert: (msg, type) => {
      // optional: show a Toast or console.log
    },
    chartData: clusterData, // If you want to embed raw data in Excel
    prepareDataForClipboard,
    customExcelLogic: customExcelTable,
  });
  
  // Ensure all clusters are present
  const allClusterData = ALL_CLUSTERS.map((clusterName) => {
    const primary = clusterData.find((c) => c.clusterName === clusterName) || {};
    const comparison = comparisonData.find(
      (c) => c.clusterName === clusterName
    ) || { "Cluster Participation %": null };

    return {
      clusterName,
      "Cluster Participation %": primary["Cluster Participation %"] || null,
      numerator: primary.numerator || null,
      denominator: primary.denominator || null,
      denominatorLabel: primary.denominatorLabel || comparison.denominatorLabel || "students",
      studentGroup: primary.studentGroup || null,
      year: primary.year || null,
      comparisonValue: comparison["Cluster Participation %"] || null,
      comparisonNumerator: comparison.numerator || null,
      comparisonDenominator: comparison.denominator || null,
    };
  });

  

  // Calculate the maximum value for Y-axis
  const maxClusterValue = Math.max(
    ...allClusterData.map((d) => d["Cluster Participation %"] || 0)
  );
  const maxComparisonValue = Math.max(
    ...allClusterData.map((d) => d.comparisonValue || 0)
  );
  const maxYValue = Math.ceil(Math.max(maxClusterValue, maxComparisonValue) / 10) * 10; // Round up to nearest 10 for a cleaner axis
  
// Title layer function
const titleLayer = (layerProps) => {
  const { width, margin } = layerProps;

  // The “chart area” excludes margins:
  const chartWidth = width - margin.left - margin.right;
  
  // We'll compute the text lines by splitting at ~1/10 of the chart area
  // Adjust the divisor to your preference so lines aren't too short/long.
  const maxLineLength = Math.floor(chartWidth / 10);

  const lines = splitTextOnWords(titleText, maxLineLength);

  // Dynamically pick font size based on line count
  const fontSize = lines.length > 2 ? "0.85rem" : "1.25rem";

  // Where to place the title
  //  - Center horizontally within the chart area
  const xCenter = (chartWidth / 2) - (margin.left * .32);

  //  - Place the title near the top margin
  //    margin.top is 120, let's keep the text ~20px below that
  //    so effectively y = 20 above the top “chart area.”
  const yPos = -(margin.top - 20);

  return (
    <g>
      <text
        x={xCenter}
        y={yPos}
        textAnchor="middle"
        style={{
          fontSize,
          fontWeight: "bold",
          fill: "#333333",
          fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
        }}
      >
        {lines.map((line, i) => (
          <tspan
            key={i}
            x={xCenter}
            dy={i === 0 ? 0 : "1.2em"}
          >
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

  const comparisonLayer = ({ bars, yScale }) => {
    return (
      <g>
        {bars.map((bar) => {
          const { clusterName } = bar.data.data;
          const comparisonValue = bar.data.data.comparisonValue;
          const numerator = bar.data.data.comparisonNumerator;
          const denominator = bar.data.data.comparisonDenominator;
          const denominatorLabel = bar.data.data.denominatorLabel;
          const year = bar.data.data.year;
  
          if (comparisonValue == null ) return null;
  
          const barY = bar.y; // Y-position of the bar
          const comparisonY = yScale(comparisonValue); // Y-position of the dashed line
          const barHeight = bar.height; // Height of the bar
          const rectHeight = 18; // Height of the visible rect
          const hoverPadding = 10; // Extra padding for the hover area
          const offsetAboveLine = -1; // Move everything above the dashed line
  
          // Determine overlap: Comparison hover area intersects with the bar
          const overlap = comparisonY < barY + barHeight;
  
          return (
            <Tippy
              key={`comparison-line-${clusterName}`}
              interactive={false}
              delay={[500, 0]} // 500ms delay before showing the tooltip
              placement="right"
              offset={[-60, 7]} // Adjust positioning relative to the cursor
              appendTo={document.body} // Ensures it's rendered in the body
              theme={theme.comparison}
              content={
                <div className="bg-white rounded-lg shadow-lg p-4 max-w-xs text-sm text-gray-800 leading-relaxed" style={{
                  border: `1px solid rgba(245, 140, 108, 0.5) `,
                }}>
                  <div className="mb-2 flex items-center" >
                    <strong className="text-lg text-slate-700">{clusterName}</strong>
                  </div>
                  <div>
                    <div className="mb-2 flex items-center">
                      <span>{formatPercent(comparisonValue)} {comparisonGroup} Participation</span>
                    </div>
                    In {year}, <strong>{numerator}</strong> out of{" "}
                    <strong>{denominator}</strong> {denominatorLabel || "students"} (<em>
                      {formatPercent(comparisonValue)}
                    </em>) participated.
                  </div>
                </div>
              }
            >
              <g>
                {/* Invisible hover area */}
                <rect
                  x={bar.x - 10} // Start of the dashed line
                  y={
                    overlap
                      ? comparisonY - rectHeight - offsetAboveLine // Restrict to rect height
                      : comparisonY - rectHeight - hoverPadding / 2 - offsetAboveLine // Allow full hover area
                  }
                  width={bar.width + 20} // Covers the entire width of the dashed line
                  height={overlap ? rectHeight : rectHeight + hoverPadding} // Adjust height if overlapping
                  fill="transparent" // Invisible but interactive
                  className="cursor-pointer"
                />
                {/* Dashed line */}
                <line
                  x1={bar.x - 10}
                  x2={bar.x + bar.width + 10}
                  y1={comparisonY}
                  y2={comparisonY}
                  stroke={comparisonColor}
                  strokeWidth={2}
                  strokeDasharray="6,4"
                />
                {/* Visible Rect */}
                <rect
                  x={bar.x + bar.width - 20}
                  y={comparisonY - rectHeight - offsetAboveLine} // Move rect above the line
                  width={29}
                  height={rectHeight}
                  rx={2}
                  fill={comparisonColor}
                />
                {/* Label */}
                <text
                  x={bar.x + bar.width - 5}
                  y={comparisonY - offsetAboveLine - rectHeight / 4} // Adjust text to align with rect
                  textAnchor="middle"
                  style={{
                    fill: "#FFF",
                    fontSize: "0.75rem",
                    fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
                  }}
                  className="fill-white text-xs font-bold"
                >
                  {formatPercent(comparisonValue)}
                </text>
              </g>
            </Tippy>
          );
        })}
      </g>
    );
  };

//custom tooltip for the bars
  const tooltipLayer = ({ bars }) => {
    return (
      <g>
        {bars.map((bar) => {
          const { clusterName, numerator, denominator, denominatorLabel, year, studentGroup } =
            bar.data.data;
          const value = bar.data.value;
  
          return (
            <Tippy
              key={`tooltip-${clusterName}`}
              interactive={false}
              delay={[500, 0]} // 500ms delay before showing the tooltip   
            placement="top"
            offset={[0, 27]} // Adjust positioning relative to the cursor
            appendTo={document.body} // Ensures it's rendered in the body
              theme={theme.primary}
              content={
                <div             style={{
                 
                  border: `1px solid #A3CAFA`,
                }}
               className="bg-white rounded-lg shadow-lg p-4 max-w-xs text-sm text-gray-800 leading-relaxed">
                  <div className="mb-2 flex items-center">
                  <strong style={{ color: "#334155", fontSize: "18px", marginRight: "8px" }}>
              {clusterName}
            </strong>
                  </div>
                  <div>
                    In {year}, <strong>{numerator}</strong> out of{" "}
                    <strong>{denominator}</strong> {denominatorLabel || "students"} ({value?.toFixed(1) || value}%).
                  </div>
                </div>
              }
            >
              <rect
                x={bar.x}
                y={bar.y}
                width={bar.width}
                height={bar.height}
                fill="transparent"
                className="cursor-pointer"
              />
            </Tippy>
          );
        })}
      </g>
    );
  };







    // We'll store the chart width in a ref
    const chartWidthRef = useRef(0);    
    const captionHeightRef = useRef(0);

     // This small layer runs on each render, grabbing "width" from Nivo.
  // We'll place it before the 'axes' layer, so it’s called prior to axis rendering.
  const captureWidthLayer = (layerProps) => {
    chartWidthRef.current = layerProps.width;
    return null; // This layer doesn’t render anything
  };

    // 3) Separate clusters with no data vs. those that have data
    const excludedClusters = [];
    let displayData = [];
  
    allClusterData.forEach((item) => {
      const val = item["Cluster Participation %"];
      if (val === null || val === undefined) {
        // no data
        excludedClusters.push(item.clusterName);
      } else {
        // keep it
        displayData.push(item);
      }
    });
  
    // 4) Preprocess masked data:
    //    If a value is negative, store the code in "Cluster Participation %_maskedReason",
    //    then set the numeric value to 0 for the chart
    displayData = displayData.map((row) => {
      const newRow = { ...row };
      const val = newRow["Cluster Participation %"];
      if (typeof val === "number" && val < 0) {
        newRow["Cluster Participation %_maskedReason"] = val;
        newRow["Cluster Participation %"] = 0; // force numeric zero
      }
      return newRow;
    });
  
    displayData.sort((a, b) => {
        const primaryA = a["Cluster Participation %"];
        const primaryB = b["Cluster Participation %"];
        const comparisonA = a.comparisonValue || 0;
        const comparisonB = b.comparisonValue || 0;
      
        // -- NEW: Check for maskedReason or null
        const isAMasked = a["Cluster Participation %_maskedReason"] != null || primaryA == null;
        const isBMasked = b["Cluster Participation %_maskedReason"] != null || primaryB == null;
      
        // 1) If all primary values are masked => sort by comparison
        const allPrimaryMasked = displayData.every(
          (item) =>
            item["Cluster Participation %_maskedReason"] != null ||
            item["Cluster Participation %"] == null
        );
        if (allPrimaryMasked) {
          return comparisonB - comparisonA;
        }
      
        // 2) If both are unmasked => sort descending by primary
        if (!isAMasked && !isBMasked) {
          return primaryB - primaryA;
        }
      
        // 3) If one is masked and other is not => put unmasked first
        if (!isAMasked && isBMasked) return -1; // A is valid => A first
        if (isAMasked && !isBMasked) return 1;  // B is valid => B first
      
        // 4) If both are masked => sort by comparison
        return comparisonB - comparisonA;
      });
  // 4) Pad with filler clusters if < 6
const minClusters = 9;
if (displayData.length < minClusters) {
  const needed = minClusters - displayData.length;
  for (let i = 1; i <= needed; i++) {
    displayData.push({
      clusterName: `filler${i}`, // unique name
      "Cluster Participation %": null,
      // no maskedReason, since it's just filler
    });
  }
}
    // 4) Configure keys, colors, etc. for Nivo
    
    const colors = { "Cluster Participation %": color };


                // 2) Figure out how many "real" clusters are rendered
                //    i.e. not filler, not excluded
                //    If you need the 'displayData' inside this layer, you can pass it down as a prop,
                //    or store it in a ref, etc. For simplicity, let's approximate:
                const realCount = displayData.filter(
                    (d) => !d.clusterName.startsWith("filler")
                  ).length;
  
    // 5) We'll define a custom bottom-axis tick that does word-wrapping

    const axisBottomTick = (props) => {
      const { value, x, y, textAnchor } = props;
      const chartWidth = chartWidthRef.current;
    
      if (!value || value.startsWith("filler")) {
        return null; // Skip fillers
      }
    
      const displayName = getClusterName(value, chartWidth, realCount);
    
      if (chartWidth < 800 || realCount > 13) {
        // Rotate 45° for smaller screens
        return (
          <g transform={`translate(${x},${y + 5}) rotate(-45 ${0} ${0})`}>
            <text
              x={0}
              y={0}
              textAnchor="end"
              style={{
                fill: "#333",
                fontSize: "0.75rem",
                fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
              }}
            >
              {displayName}
            </text>
          </g>
        );
      } else {
        // Multi-line for larger screens
        const lines = splitTextOnWords(displayName, 12); // Adjust max line length
        return (
          <g transform={`translate(${x},${y + 22})`}>
            {lines.map((line, i) => (
              <text
                key={i}
                x={0}
                y={i * 14}
                textAnchor={textAnchor || "middle"}
                style={{
                  fill: "#333",
                  fontSize: "0.75rem",
                  fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
                }}
              >
                {line}
              </text>
            ))}
          </g>
        );
      }
    };
                 
  // 6) Return the bar chart
  return (
    <div>  {/* 2) Export controls */}
      <div className="flex justify-end mb-2">
        <ExportPopover
          onCopyImage={copyImageToClipboard}
          onCopyData={copyDataToClipboard}
          onExportPNG={exportChartAsPNG}
          onExportExcel={exportChartToExcel}
          // onCopyData, onCopyImage, etc.
        />
        </div>
    <div ref={chartRef} className="w-full h-[500px] my-4 pr-12">
     
      
      <ResponsiveBar
      
        data={displayData}
        keys={keys}
        maxValue={maxYValue}
        indexBy="clusterName"
       isInteractive={false}
        layout="vertical"
        label={null}
        margin={{ top: 120, right: 0, bottom: 125, left: 110 }}
        padding={0.3}
        innerPadding={3}
        groupMode="grouped"
        colors={({ id }) => colors[id]}
        borderWidth={3}
        borderRadius={3}
        borderColor={{
          from: "color",
          modifiers: [["brighter", "0.3"]],
        }}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,  // no rotation
          renderTick: axisBottomTick,
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: "Percentage (%)",
          legendPosition: "middle",
          legendOffset: -60,
          tickValues: 5,
        }}
        theme={{
            fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
            grid: {
              line: {
                stroke: "#a3a3a3",
                strokeWidth: 1,
                strokeDasharray: "6,4",
              },
            },
            axis: {
              domain: {
                line: {
                  stroke: "#777777",
                  strokeWidth: 1,
                },
              },
              ticks: {
                line: {
                  stroke: "#777777",
                  strokeWidth: 1,
                },
                text: {
                  fill: "#333333",
                  fontSize: "0.875rem",
                  fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
                },
              },
              legend: {
                text: {
                  fill: "#333333",
                  fontSize: "1rem",
                  fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
                },
              },
            },
            legends: {
              text: {
                fill: "#333333",
                fontSize: "0.875rem",
                fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
              },
            },
          }}
        legends={[]}
        // 7) Add layers for your masked logic, full bar patterns, etc.
        layers={[
            captureWidthLayer,
            
            titleLayer,
            // 2) Custom Grid Lines Layer
            ({ yScale, width }) => {
              const ticks = yScale.ticks ? yScale.ticks(5) : yScale.domain();

              return (
                <g>
                  {ticks.map((tick, i) => (
                    <line
                      key={i}
                      x1={0}
                      x2={width}
                      y1={yScale(tick)}
                      y2={yScale(tick)}
                      stroke="#a3a3a3"
                      strokeWidth={0.5}
                      strokeDasharray="6,4"
                    />
                  ))}
                </g>
              );
            }, // 7) A custom layer to show excluded clusters
            ({ width, height }) => {
                if (excludedClusters.length === 0) return null;
              
                // 1) Sort alphabetically
                const sortedExcluded = [...excludedClusters].sort();
              

              
                // 3) Dynamically pick an x offset
                //    e.g., if fewer than 3 real clusters, place caption around x=200,
                //    else around x=700. Adjust to taste.
                const startX = Math.min(width - 442, width / minClusters * realCount);
              
                // 4) We'll place it near the top or somewhere vertical
                //    For example, 50px down from the top
                const startY = 0;
              
                const fontSize = 13;
                const lineHeight = 18;
              
                // 5) Let's compute how tall our caption needs to be:
                //    1 line for the heading, plus an underline, plus 1 line per excluded cluster
                //    plus some bottom padding
                const headingLines = 1; // just a single heading line
                const bulletCount = sortedExcluded.length;
                const totalHeight =
                  headingLines * lineHeight + 10 + bulletCount * lineHeight + 20; 
                  // 10 for spacing between heading & underline
                  // 20 bottom padding
                  captionHeightRef.current = totalHeight;
                const captionWidth = 330; // how wide we want the box to be
                const boxPadding = 10;    // padding inside the rect
              
                return (
                  <g transform={`translate(${startX}, ${startY})`}>
                    {/* 6) Background rectangle */}
                    <rect
                      x={0}
                      y={0}
                      width={captionWidth}
                      height={totalHeight}
                      fill="#f1f1f1"         // light gray
                      stroke="#d4d4d8"       // slightly darker border
                      strokeWidth={1}
                      rx={8}                 // rounded corners
                      ry={8}
                    />
              
                    {/* 7) Heading text */}
                    <text
                      x={boxPadding}
                      y={lineHeight}
                      textAnchor="start"
                      style={{
                        fill: "#374151",
                        fontSize,
                        fontWeight: "bold",
                        fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
                      }}
                    >
                      The following clusters had no enrollment data:
                    </text>
              
                    {/* 7a) Underline the heading using a <line> */}
                    <line
                      x1={boxPadding}
                      y1={lineHeight + 4} // 4px below the text baseline
                      x2={captionWidth - boxPadding}
                      y2={lineHeight + 4}
                      stroke="#9ca3af" // mid-gray
                      strokeWidth={1}
                    />
              
                    {/* 8) Render bullet items for each excluded cluster */}
                    {sortedExcluded.map((name, i) => {
                      const bulletY = lineHeight * (2 + i); 
                      // 2 lines above the bullet items => heading line + space/underline
                      return (
                        <g key={name} transform={`translate(0, ${bulletY})`}>
                          {/* bullet */}
                          <text
                            x={boxPadding}
                            y={0}
                            textAnchor="start"
                            style={{
                              fill: "#374151",
                              fontSize,
                              fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
                            }}
                          >
                            •
                          </text>
                          {/* cluster name */}
                          <text
                            x={boxPadding + 15}
                            y={0}
                            textAnchor="start"
                            style={{
                              fill: "#374151",
                              fontSize,
                              fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
                            }}
                          >
                            {name}
                          </text>
                        </g>
                      );
                    })}
                  </g>
                );
              },
            // 3) Custom layer for the bars themselves
            "bars",
            
           // 4) A custom layer for the pattern <defs> (so it's available for masked bars)
  () => (
    <defs>
      <pattern
        id="maskDiagonal"
        patternUnits="userSpaceOnUse"
        width="6"
        height="6"
        patternTransform="rotate(45)"  
      >
        {/* This white rect is the background of the pattern tile */}
        {/* <rect x="0" y="0" width="6" height="6" fill="white" /> */}

        {/* A single diagonal line repeating every 6px */}
        <line
          x1="0"
          y1="0"
          x2="0"
          y2="6"
          stroke="#e4e4e7"
          strokeWidth="1.5"
        />
      </pattern>
    </defs>
  ),

  // 5) Custom layer for masked placeholders & labels
  ({ bars, yScale }) => {
    // figure out the maximum value in the yScale domain
    // e.g., if domain is [0, 100], maxVal = 100
    const domain = yScale.domain();
    const maxVal = Math.max(...domain);

    return bars.map((bar) => {
      const reasonKey = `${bar.data.id}_maskedReason`;
      const maskedVal = bar.data.data[reasonKey]; // e.g. -1, -2, etc.
      const numericVal = bar.data.value;          // e.g. 0 if negative originally
      const clusterName = bar.data.data.clusterName;
      if (clusterName && clusterName.startsWith("filler")) {
        return null; // skip label
      }
      let labelText;
      
      if (maskedVal !== undefined) {
        // We have a negative code
        const friendlyReason =
          negativeReasons[String(maskedVal)] || "Masked";
        // Example multiline label:
        // "Masked - Top-Bottom DAT"
        // "(-1)"
        labelText = `Masked\n${friendlyReason}`;
      } else {
        // Normal bar
        labelText = formatPercent(numericVal);
      }

      // If masked => draw a full bar with diagonal pattern
      if (maskedVal !== undefined) {
        // We want to draw a bar from "0%" to "maxVal%".
        // Because Nivo draws from top (yScale(maxVal)) down to bottom (yScale(0)).
        const barY = yScale(maxVal);
        const barHeight = yScale(0) - barY;

        // We'll center the text in this bar:
        const centerX = bar.x + bar.width / 2;
        const centerY = barY + barHeight / 2;

        // For multiline label, we can split on `\n`
        const lines = labelText.split("\n");

        return (
          <g key={bar.key}>
            {/* full-height rectangle */}
            <rect
              x={bar.x}
              y={barY + captionHeightRef.current + 10}
              width={bar.width}
              height={barHeight - captionHeightRef.current - 10 }
              fill="url(#maskDiagonal)"  // Use the diagonal pattern
              fillOpacity={1}         // Slightly visible
              stroke="#e4e4e7"
              strokeWidth={1}
            />
            {/* multiline masked label, centered */}
            <text
              x={centerX}
              y={centerY + captionHeightRef.current / 2}
              textAnchor="middle"
              alignmentBaseline="middle"
              style={{
                fill: "#71717a",
                fontWeight: "semibold",
                fontStyle: "italic",
                fontSize: "0.7rem",
                fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
              }}
            >
              {lines.map((line, i) => (
                <tspan
                  key={i}
                  x={centerX}
                  dy={i === 0 ? 0 : "1.2em"}
                >
                  {line}
                </tspan>
              ))}
            </text>
          </g>
        );
      } else {
        // Normal bar => just draw a label above it
        return (
          <g key={bar.key}>
            <rect
              x={bar.x + bar.width / 2 - 21}
              y={bar.y - 22}
              width={44}
              height={16}
              fill="#ffffff"
            />
            <text
              x={bar.x + bar.width / 2}
              y={bar.y - 6}
              textAnchor="middle"
              style={{
                fill: "#4B5563",
                fontWeight: "bold",
                fontSize: "0.875rem",
                fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
              }}
            >
              {labelText}
            </text>
          </g>
        );
      }
    });
  },
            "axes",
            "markers",
            "legends",
            tooltipLayer,
            comparisonLayer,
          ]}
      />
     
    </div></div>
  );
};

export default ClusterParticipationChart;